import { collapseHoursToRanges, MAX_WINDOWS, selectedHoursFromSlots, type HourSlotMap } from '@/utils/hourlyBudgetHourSlots';
import {
  ALL_WEEKDAYS,
  cloneHourSlots,
  emptyWeeklySlots,
  WEEKDAYS,
  type WeekdayIndex,
} from '@/utils/hourlyBudgetWeek';

export const DAYPARTING_CREATE_PREFILL_KEY = 'daypartingPrefill';

const WEEKDAY_NAME_TO_INDEX: Record<string, WeekdayIndex> = {
  Monday: 0,
  Tuesday: 1,
  Wednesday: 2,
  Thursday: 3,
  Friday: 4,
  Saturday: 5,
  Sunday: 6,
};

export type HeatmapPrefillCell = {
  day: string;
  rowCount: number;
};

export type HeatmapPrefillHour = {
  hour: number;
  cells: HeatmapPrefillCell[];
};

export type DaypartingCreatePrefill = {
  weeklySlots: Record<WeekdayIndex, HourSlotMap>;
  campaignId?: string;
  campaignIds?: string[];
  portfolioId?: string;
};

export type DaypartingPrefillSummary = {
  slotCount: number;
  weekdayIndexes: WeekdayIndex[];
  exceedsMaxWindows: boolean;
};

export function cloneWeeklySlots(
  slots: Record<WeekdayIndex, HourSlotMap>
): Record<WeekdayIndex, HourSlotMap> {
  const next = emptyWeeklySlots();
  for (const day of ALL_WEEKDAYS) {
    next[day] = cloneHourSlots(slots[day] ?? {});
  }
  return next;
}

export function weeklySlotsFromMatchingHeatmapHours(
  hours: HeatmapPrefillHour[]
): Record<WeekdayIndex, HourSlotMap> {
  const slots = emptyWeeklySlots();
  for (const hourEntry of hours) {
    const hour = hourEntry.hour;
    if (!Number.isInteger(hour) || hour < 0 || hour > 23) continue;
    for (const cell of hourEntry.cells) {
      if (cell.rowCount <= 0) continue;
      const day = WEEKDAY_NAME_TO_INDEX[cell.day];
      if (day == null) continue;
      slots[day][hour] = '1';
    }
  }
  return slots;
}

export function matchingSlotCount(slots: Record<WeekdayIndex, HourSlotMap>): number {
  return ALL_WEEKDAYS.reduce<number>(
    (sum, day) => sum + selectedHoursFromSlots(slots[day] ?? {}).length,
    0
  );
}

export function matchingWeekdayIndexes(slots: Record<WeekdayIndex, HourSlotMap>): WeekdayIndex[] {
  return ALL_WEEKDAYS.filter((day) => selectedHoursFromSlots(slots[day] ?? {}).length > 0);
}

export function weekdayExceedsMaxWindows(slots: Record<WeekdayIndex, HourSlotMap>): boolean {
  return ALL_WEEKDAYS.some(
    (day) => collapseHoursToRanges(selectedHoursFromSlots(slots[day] ?? {})).length > MAX_WINDOWS
  );
}

export function canCreateDaypartingAutomation(options: {
  completeFilterCount: number;
  weeklySlots: Record<WeekdayIndex, HourSlotMap>;
}): boolean {
  return options.completeFilterCount > 0 && matchingSlotCount(options.weeklySlots) > 0;
}

export function summarizeDaypartingPrefill(
  slots: Record<WeekdayIndex, HourSlotMap>
): DaypartingPrefillSummary {
  return {
    slotCount: matchingSlotCount(slots),
    weekdayIndexes: matchingWeekdayIndexes(slots),
    exceedsMaxWindows: weekdayExceedsMaxWindows(slots),
  };
}

export function formatPrefillWeekdays(days: WeekdayIndex[]): string {
  if (days.length === 0) return '';
  return days.map((day) => WEEKDAYS[day].short).join(', ');
}

export function parseDaypartingCreatePrefill(state: unknown): DaypartingCreatePrefill | null {
  if (!state || typeof state !== 'object') return null;
  const raw = (state as Record<string, unknown>)[DAYPARTING_CREATE_PREFILL_KEY];
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  if (!obj.weeklySlots || typeof obj.weeklySlots !== 'object') return null;

  const weeklySlots = emptyWeeklySlots();
  let hasAny = false;
  const rawSlots = obj.weeklySlots as Record<string, unknown>;
  for (const day of ALL_WEEKDAYS) {
    const daySlots = rawSlots[day] ?? rawSlots[String(day)];
    if (!daySlots || typeof daySlots !== 'object' || Array.isArray(daySlots)) continue;
    const next: HourSlotMap = {};
    for (const hourKey of Object.keys(daySlots as Record<string, unknown>)) {
      const hour = Number(hourKey);
      if (!Number.isInteger(hour) || hour < 0 || hour > 23) continue;
      next[hour] = '1';
      hasAny = true;
    }
    weeklySlots[day] = next;
  }
  if (!hasAny) return null;

  const campaignId =
    typeof obj.campaignId === 'string' && obj.campaignId.trim() ? obj.campaignId.trim() : undefined;
  const campaignIds = Array.isArray(obj.campaignIds)
    ? obj.campaignIds.filter((id): id is string => typeof id === 'string' && Boolean(id.trim())).map((id) => id.trim())
    : campaignId
      ? [campaignId]
      : undefined;
  const portfolioId =
    typeof obj.portfolioId === 'string' && obj.portfolioId.trim() ? obj.portfolioId.trim() : undefined;
  return {
    weeklySlots,
    campaignId: campaignIds?.[0] ?? campaignId,
    campaignIds,
    portfolioId,
  };
}
